// HeroChatPanel.tsx - Glass panel hero chatbox with rotating placeholder hints
import React, { useState, useEffect, useRef } from 'react';
import './HeroChatPanel.css';
import { CLINIC_CONFIG } from '../../../config/clinicConfig';
import { preconnectWebSocket } from '../preconnectWebSocket';

// Default phrases that can be overridden per page
export const DEFAULT_PHRASES = [
  "Verify my insurance coverage…",
  "What are the costs & benefits?",
  "Do you accept Blue Cross Blue Shield?",
  "Book an appointment for a cleaning…",
];

// Pending image data for passing to chatbox
export interface PendingImageData {
  file: File;
  previewUrl: string;
}

interface HeroChatPanelProps {
  phrases?: string[];
  clinicName?: string;
  onSubmit: (message: string, images?: PendingImageData[]) => void;
}

const HeroChatPanel: React.FC<HeroChatPanelProps> = ({
  phrases = DEFAULT_PHRASES,
  clinicName = CLINIC_CONFIG.clinicName,
  onSubmit,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [selectedImages, setSelectedImages] = useState<PendingImageData[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const warmChatbotConnection = () => {
    preconnectWebSocket(CLINIC_CONFIG.aiAgent);
  };

  // Rotate placeholder phrases
  useEffect(() => {
    const interval = setInterval(() => {
      if (inputValue) return; // Don't rotate if user is typing
      
      setIsFadingOut(true);
      setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        setIsFadingOut(false);
      }, 220);
    }, 2200);

    return () => clearInterval(interval);
  }, [inputValue, phrases.length]);

  // Handle file selection for insurance card upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    warmChatbotConnection();
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validImages: PendingImageData[] = [];

    for (let i = 0; i < Math.min(files.length, 2); i++) {
      const file = files[i];
      
      if (!allowedTypes.includes(file.type)) continue;
      if (file.size > maxSize) continue;

      const previewUrl = file.type === 'application/pdf' ? 'pdf' : URL.createObjectURL(file);
      validImages.push({ file, previewUrl });
    }

    if (validImages.length > 0) {
      setSelectedImages(validImages);
    }

    event.target.value = '';
  };

  // Clear selected images
  const handleClearImages = () => {
    selectedImages.forEach(img => {
      if (img.previewUrl !== 'pdf') URL.revokeObjectURL(img.previewUrl);
    });
    setSelectedImages([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    
    // Allow submit if there's text OR images
    if (!trimmed && selectedImages.length === 0) return;

    // Pass both message and images to parent
    onSubmit(trimmed || 'Please analyze my insurance card', selectedImages.length > 0 ? selectedImages : undefined);
    setInputValue('');
    setSelectedImages([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    warmChatbotConnection();
    setInputValue(e.target.value);
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="hero-chat-wrap">
      <div className="hero-chat-panel">
        <div className="hero-chat-headline">
          I'm your treatment coordinator.{' '}
          <span className="hero-chat-clinic">{clinicName}</span>.{' '}
          Ask me anything.
        </div>

        {/* Image preview bar */}
        {selectedImages.length > 0 && (
          <div className="hero-chat-preview">
            {selectedImages.map((img, idx) => (
              <div key={idx} className="hero-chat-preview-item">
                {img.previewUrl === 'pdf' ? (
                  <div className="hero-chat-pdf-badge">PDF</div>
                ) : (
                  <img src={img.previewUrl} alt={`Preview ${idx + 1}`} />
                )}
              </div>
            ))}
            <button 
              type="button" 
              className="hero-chat-clear-btn"
              onClick={handleClearImages}
              aria-label="Clear selected files"
            >
              <i className="bi bi-x-circle"></i>
            </button>
          </div>
        )}

        <form className="hero-chat-search" onSubmit={handleSubmit}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp,application/pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Upload insurance card"
          />

          {/* Image upload button */}
          <button 
            type="button" 
            className="hero-chat-upload-btn"
            onClick={handleImageButtonClick}
            aria-label="Upload insurance card"
            title="Upload insurance card (image or PDF)"
          >
            <i className="bi bi-upload"></i>
          </button>

          <input
            ref={inputRef}
            className="hero-chat-input"
            type="text"
            aria-label="Ask a question"
            autoComplete="off"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={warmChatbotConnection}
            placeholder={selectedImages.length > 0 ? "Add a message (optional)..." : ""}
          />
          <span 
            className={`hero-chat-hint ${isFadingOut ? 'fadeout' : ''}`}
            aria-hidden="true"
            style={{ opacity: (inputValue || selectedImages.length > 0) ? 0 : 0.55 }}
          >
            {phrases[currentPhraseIndex]}
          </span>

          <button 
            className="hero-chat-btn" 
            type="submit" 
            aria-label="Send"
            disabled={!inputValue.trim() && selectedImages.length === 0}
          >
            <i className="bi bi-arrow-return-left"></i>
          </button>
        </form>
      </div>
    </div>
  );
};

export default HeroChatPanel;

